import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { sendInterestConfirmationEmail } from '../lib/email';
import {
  CalendarDaysIcon,
  UserGroupIcon,
  HomeModernIcon,
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

const INVITATION_SOURCES = [
  'Direct invitation from organiser',
  'Referred by a friend or colleague',
  'Email newsletter',
  'Social media',
  'Corporate / sponsor invitation',
  'Golf association',
  'Previous attendee',
  'Other',
];

const DETAIL_REQUIRED_SOURCES = ['Corporate / sponsor invitation', 'Other'];

const schema = z.object({
  first_name: z.string().min(2, 'First name is required'),
  last_name: z.string().min(2, 'Last name is required'),
  email: z.string().email('A valid email is required'),
  phone: z.string().min(7, 'Phone number is required'),
  nationality: z.string().optional(),
  invitation_source: z.string().min(1, 'Please tell us how you heard about this invitation'),
  invitation_source_detail: z.string().optional(),
  arrival_date: z.string().min(1, 'Arrival date is required'),
  departure_date: z.string().min(1, 'Departure date is required'),
  num_adults: z.coerce.number().int().min(1, 'At least 1 adult required').max(20),
  num_children: z.coerce.number().int().min(0).max(20),
  room_type: z.string().min(1, 'Please select an accommodation preference'),
  special_requests: z.string().optional(),
}).refine(data => {
  if (!data.arrival_date || !data.departure_date) return true;
  return new Date(data.departure_date) >= new Date(data.arrival_date);
}, { message: 'Departure must be on or after arrival', path: ['departure_date'] }).refine(data => {
  if (DETAIL_REQUIRED_SOURCES.includes(data.invitation_source)) {
    return !!data.invitation_source_detail && data.invitation_source_detail.trim().length > 0;
  }
  return true;
}, {
  message: 'Please provide more details',
  path: ['invitation_source_detail'],
});

type FormData = z.infer<typeof schema>;

interface FormSettings {
  published: boolean;
  openDate: string | null;
  closeDate: string | null;
}

interface AccommodationType {
  id: string;
  name: string;
  description: string;
  cost_per_night: number;
  currency: string;
  sort_order: number;
}

const FALLBACK_ROOM_OPTIONS: AccommodationType[] = [
  { id: 'none', name: 'No room required', description: 'I have my own accommodation', cost_per_night: 0, currency: 'USD', sort_order: 0 },
  { id: 'standard', name: 'Standard Room', description: 'Comfortable room with golf course views', cost_per_night: 0, currency: 'USD', sort_order: 1 },
  { id: 'deluxe', name: 'Deluxe Room', description: 'Spacious room with premium amenities', cost_per_night: 0, currency: 'USD', sort_order: 2 },
  { id: 'suite', name: 'Suite', description: 'Luxury suite with lounge and panoramic views', cost_per_night: 0, currency: 'USD', sort_order: 3 },
  { id: 'villa', name: 'Villa', description: 'Private villa with exclusive facilities', cost_per_night: 0, currency: 'USD', sort_order: 4 },
];

function formatCost(cost: number, currency: string): string {
  if (cost === 0) return '';
  return `${currency} ${cost.toLocaleString()}/night`;
}

const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Antiguan', 'Argentine',
  'Armenian', 'Australian', 'Austrian', 'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi',
  'Barbadian', 'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese', 'Bolivian',
  'Bosnian', 'Botswanan', 'Brazilian', 'Bruneian', 'Bulgarian', 'Burkinabe', 'Burundian',
  'Cambodian', 'Cameroonian', 'Canadian', 'Cape Verdean', 'Central African', 'Chadian',
  'Chilean', 'Chinese', 'Colombian', 'Comorian', 'Congolese', 'Costa Rican', 'Croatian',
  'Cuban', 'Cypriot', 'Czech', 'Danish', 'Djiboutian', 'Dominican', 'Dutch', 'Ecuadorean',
  'Egyptian', 'Emirati', 'Equatorial Guinean', 'Eritrean', 'Estonian', 'Eswatini', 'Ethiopian',
  'Fijian', 'Finnish', 'French', 'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian',
  'Greek', 'Grenadian', 'Guatemalan', 'Guinean', 'Guinea-Bissauan', 'Guyanese', 'Haitian',
  'Honduran', 'Hungarian', 'Icelandic', 'Indian', 'Indonesian', 'Iranian', 'Iraqi', 'Irish',
  'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese', 'Jordanian', 'Kazakhstani',
  'Kenyan', 'Kiribatian', 'Kuwaiti', 'Kyrgyzstani', 'Laotian', 'Latvian', 'Lebanese',
  'Lesothan', 'Liberian', 'Libyan', 'Liechtensteiner', 'Lithuanian', 'Luxembourger',
  'Macedonian', 'Malagasy', 'Malawian', 'Malaysian', 'Maldivian', 'Malian', 'Maltese',
  'Marshallese', 'Mauritanian', 'Mauritian', 'Mexican', 'Micronesian', 'Moldovan', 'Monegasque',
  'Mongolian', 'Montenegrin', 'Moroccan', 'Mozambican', 'Namibian', 'Nauruan', 'Nepalese',
  'New Zealander', 'Nicaraguan', 'Nigerian', 'Nigerien', 'North Korean', 'Norwegian', 'Omani',
  'Pakistani', 'Palauan', 'Palestinian', 'Panamanian', 'Papua New Guinean', 'Paraguayan',
  'Peruvian', 'Filipino', 'Polish', 'Portuguese', 'Qatari', 'Romanian', 'Russian', 'Rwandan',
  'Saint Kittsian', 'Saint Lucian', 'Saint Vincentian', 'Samoan', 'San Marinese', 'Sao Tomean',
  'Saudi', 'Senegalese', 'Serbian', 'Seychellois', 'Sierra Leonean', 'Singaporean', 'Slovak',
  'Slovenian', 'Solomon Islander', 'Somali', 'South African', 'South Korean', 'South Sudanese',
  'Spanish', 'Sri Lankan', 'Sudanese', 'Surinamese', 'Swedish', 'Swiss', 'Syrian',
  'Taiwanese', 'Tajikistani', 'Tanzanian', 'Thai', 'Timorese', 'Togolese', 'Tongan',
  'Trinidadian', 'Tunisian', 'Turkish', 'Turkmen', 'Tuvaluan', 'Ugandan', 'Ukrainian',
  'Uruguayan', 'Uzbekistani', 'Vanuatuan', 'Venezuelan', 'Vietnamese', 'Yemeni', 'Zambian',
  'Zimbabwean',
];

function generateReference(): string {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `SHIP-${date}-${rand}`;
}

export default function InterestForm() {
  const [formSettings, setFormSettings] = useState<FormSettings>({ published: false, openDate: null, closeDate: null });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [accommodationTypes, setAccommodationTypes] = useState<AccommodationType[]>(FALLBACK_ROOM_OPTIONS);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      num_adults: 1,
      num_children: 0,
      room_type: '',
      invitation_source: '',
      invitation_source_detail: '',
    },
  });

  useEffect(() => {
    fetchSettings();
    fetchAccommodationTypes();
  }, []);

  const fetchAccommodationTypes = async () => {
    try {
      const { data } = await supabase
        .from('invitation_accommodation_types')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (data && data.length > 0) {
        setAccommodationTypes(data);
      }
    } catch {
    }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['interest_form_published', 'interest_form_open_date', 'interest_form_close_date']);

      if (data) {
        const map: Record<string, string> = {};
        data.forEach(row => {
          const raw = row.value;
          map[row.key] = typeof raw === 'string' ? raw.replace(/^"|"$/g, '') : String(raw);
        });
        setFormSettings({
          published: map['interest_form_published'] === 'true',
          openDate: map['interest_form_open_date'] || null,
          closeDate: map['interest_form_close_date'] || null,
        });
      }
    } catch {
    } finally {
      setSettingsLoading(false);
    }
  };

  const isWithinWindow = () => {
    const now = new Date();
    if (formSettings.openDate && new Date(formSettings.openDate) > now) return false;
    if (formSettings.closeDate && new Date(formSettings.closeDate) < now) return false;
    return true;
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    const reference = generateReference();
    try {
      const baseInsert = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        nationality: data.nationality || '',
        arrival_date: data.arrival_date,
        departure_date: data.departure_date,
        num_adults: data.num_adults,
        num_children: data.num_children,
        room_type: data.room_type,
        special_requests: data.special_requests || '',
        reference,
        status: 'new',
      };

      let result = await supabase.from('interest_submissions').insert({
        ...baseInsert,
        invitation_source: data.invitation_source,
        invitation_source_detail: data.invitation_source_detail || '',
      });
      if (result.error && (result.error.message.includes('invitation_source_detail'))) {
        result = await supabase.from('interest_submissions').insert({ ...baseInsert, invitation_source: data.invitation_source });
      }
      if (result.error && result.error.message.includes('invitation_source')) {
        result = await supabase.from('interest_submissions').insert(baseInsert);
      }
      if (result.error) throw result.error;

      await sendInterestConfirmationEmail(
        data.email,
        data.first_name,
        data.last_name,
        reference,
        data.arrival_date,
        data.departure_date,
        data.room_type,
        data.num_adults,
        data.num_children,
      );

      setSubmittedRef(reference);
      setSubmitted(true);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-golf-sand">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  const now = new Date();
  const isClosed = formSettings.closeDate ? new Date(formSettings.closeDate) < now : false;
  const isNotYetOpen = formSettings.openDate ? new Date(formSettings.openDate) > now : false;
  const isAvailable = formSettings.published && isWithinWindow();

  if (!isAvailable) {
    return <UnavailableScreen isClosed={isClosed} isNotYetOpen={isNotYetOpen} openDate={formSettings.openDate} closeDate={formSettings.closeDate} />;
  }

  if (submitted) {
    return <SuccessScreen reference={submittedRef} />;
  }

  return (
    <div className="min-h-screen bg-golf-sand">
      <div className="relative bg-golf-gray text-white pt-36 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,26,26,0.3) 0%, rgba(26,26,26,0.75) 100%)' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.2em] uppercase text-white/40 font-medium mb-3">You are invited to attend</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-5 leading-tight">
            Ibom Open 2026
          </h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto leading-relaxed">
            We are delighted to extend this invitation to you. Please complete the form below to confirm your attendance, travel dates, and accommodation preferences.
          </p>
          {formSettings.closeDate && (
            <div className="mt-8 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 text-sm text-white/60">
              <ClockIcon className="w-4 h-4 text-golf-orange" />
              RSVP by <span className="text-white/80 font-medium ml-1">{new Date(formSettings.closeDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

          <Section icon={<UserGroupIcon className="w-5 h-5" />} title="Guest Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First Name" error={errors.first_name?.message}>
                <input {...register('first_name')} placeholder="John" className={inputClass(!!errors.first_name)} />
              </Field>
              <Field label="Last Name" error={errors.last_name?.message}>
                <input {...register('last_name')} placeholder="Doe" className={inputClass(!!errors.last_name)} />
              </Field>
              <Field label="Email Address" error={errors.email?.message}>
                <input {...register('email')} type="email" placeholder="john@example.com" className={inputClass(!!errors.email)} />
              </Field>
              <Field label="Phone Number" error={errors.phone?.message}>
                <input {...register('phone')} type="tel" placeholder="+234 800 000 0000" className={inputClass(!!errors.phone)} />
              </Field>
              <Field label="Nationality" error={errors.nationality?.message} className="sm:col-span-2">
                <NationalityAutocomplete register={register} />
              </Field>
            </div>
          </Section>

          <Section icon={<ChatBubbleLeftEllipsisIcon className="w-5 h-5" />} title="Source of Invitation">
            <Field label="How did you hear about this invitation?" error={errors.invitation_source?.message}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {INVITATION_SOURCES.map(src => {
                  const selected = watch('invitation_source') === src;
                  return (
                    <label key={src} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${selected ? 'border-golf-orange bg-golf-orange/5' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                      <input type="radio" {...register('invitation_source')} value={src} className="accent-golf-orange" />
                      <span className={`text-sm font-medium ${selected ? 'text-golf-orange' : 'text-golf-gray'}`}>{src}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
            {DETAIL_REQUIRED_SOURCES.includes(watch('invitation_source')) && (
              <Field
                label={watch('invitation_source') === 'Corporate / sponsor invitation' ? 'Please specify the Corporate / Sponsor name' : 'Please specify'}
                error={errors.invitation_source_detail?.message}
                className="mt-4"
              >
                <input
                  {...register('invitation_source_detail')}
                  placeholder={watch('invitation_source') === 'Corporate / sponsor invitation' ? 'e.g. Ship Afrika' : 'Please describe...'}
                  className={inputClass(!!errors.invitation_source_detail)}
                />
              </Field>
            )}
          </Section>

          <Section icon={<CalendarDaysIcon className="w-5 h-5" />} title="Attendance & Travel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Intended Arrival Date" error={errors.arrival_date?.message}>
                <input {...register('arrival_date')} type="date" className={`${inputClass(!!errors.arrival_date)} [color-scheme:light]`} />
              </Field>
              <Field label="Intended Departure Date" error={errors.departure_date?.message}>
                <input {...register('departure_date')} type="date" className={`${inputClass(!!errors.departure_date)} [color-scheme:light]`} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Number of Adults" error={errors.num_adults?.message}>
                <input {...register('num_adults')} type="number" min={1} max={20} className={inputClass(!!errors.num_adults)} />
              </Field>
              <Field label="Number of Children" error={errors.num_children?.message}>
                <input {...register('num_children')} type="number" min={0} max={20} className={inputClass(!!errors.num_children)} />
              </Field>
            </div>
          </Section>

          <Section icon={<HomeModernIcon className="w-5 h-5" />} title="Accommodation Preference">
            <p className="text-sm text-gray-500 mb-4">Select your preferred room type at the Resort. Final availability will be confirmed upon acceptance of your invitation.</p>
            <div className="space-y-3">
              {accommodationTypes.map(opt => {
                const selected = watch('room_type') === opt.id;
                const costStr = formatCost(opt.cost_per_night, opt.currency);
                return (
                  <label key={opt.id} className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${selected ? 'border-golf-orange bg-golf-orange/5' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                    <input type="radio" {...register('room_type')} value={opt.id} className="mt-0.5 accent-golf-orange" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className={`font-medium text-sm ${selected ? 'text-golf-orange' : 'text-golf-gray'}`}>{opt.name}</div>
                        {costStr && <div className={`text-xs font-semibold flex-shrink-0 ${selected ? 'text-golf-orange' : 'text-gray-500'}`}>{costStr}</div>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {errors.room_type && <p className="text-red-500 text-xs mt-2">{errors.room_type.message}</p>}
          </Section>

          <Section icon={null} title="Additional Notes">
            <Field label="Any dietary requirements, accessibility needs, or special requests?" error={errors.special_requests?.message}>
              <textarea
                {...register('special_requests')}
                rows={3}
                placeholder="Please let us know of any requirements we should accommodate..."
                className={`${inputClass(false)} resize-none`}
              />
            </Field>
          </Section>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-3">
            <LockClosedIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-500 leading-relaxed">
              Your details are handled in strict confidence and will only be used in connection with your invitation to the Ibom Open Golf Tournament. Our team will be in touch to confirm your place and arrange the finer details.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-golf-orange text-white font-semibold rounded-2xl hover:bg-golf-orange/90 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed text-base"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Sending RSVP...
              </span>
            ) : 'Accept Invitation'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50">
        {icon && <span className="text-golf-orange">{icon}</span>}
        <h2 className="font-semibold text-golf-gray text-sm">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, error, children, className = '' }: { label: string; error?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function NationalityAutocomplete({ register }: { register: ReturnType<typeof useForm<FormData>>['register'] }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { onChange, onBlur, name, ref } = register('nationality');

  const filtered = query.length > 0
    ? NATIONALITIES.filter(n => n.toLowerCase().startsWith(query.toLowerCase())).slice(0, 8)
    : [];

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        name={name}
        ref={el => {
          ref(el);
          inputRef.current = el;
        }}
        onBlur={onBlur}
        value={query}
        autoComplete="off"
        placeholder="e.g. Nigerian, South African..."
        className={inputClass(false)}
        onChange={e => {
          setQuery(e.target.value);
          onChange(e);
          updateDropdownPosition();
          setOpen(true);
        }}
        onFocus={() => {
          if (query) {
            updateDropdownPosition();
            setOpen(true);
          }
        }}
        onKeyDown={e => {
          if (!open || filtered.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') {
            e.preventDefault();
            const chosen = filtered[highlighted];
            if (chosen) {
              setQuery(chosen);
              onChange({ target: { name, value: chosen } } as any);
              setOpen(false);
            }
          } else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && filtered.length > 0 && (
        <ul
          style={{ position: 'fixed', top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto max-h-56"
        >
          {filtered.map((nat, i) => (
            <li
              key={nat}
              onMouseDown={e => {
                e.preventDefault();
                setQuery(nat);
                onChange({ target: { name, value: nat } } as any);
                setOpen(false);
              }}
              onMouseEnter={() => setHighlighted(i)}
              className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${i === highlighted ? 'bg-golf-orange/10 text-golf-orange' : 'text-gray-800 hover:bg-gray-50'}`}
            >
              {nat}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function inputClass(hasError: boolean) {
  return `w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 transition-colors ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-golf-orange/30 focus:border-golf-orange'}`;
}

function SuccessScreen({ reference }: { reference: string }) {
  return (
    <div className="min-h-screen bg-golf-sand flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircleIcon className="w-9 h-9 text-green-500" />
        </div>
        <h1 className="font-display text-2xl font-bold text-golf-gray mb-3">RSVP Confirmed</h1>
        <p className="text-gray-500 mb-6 leading-relaxed">
          Thank you for accepting your invitation to the Ibom Open. A confirmation has been sent to your email. Our team will be in touch shortly to finalise your arrangements.
        </p>
        <div className="bg-golf-sand rounded-xl p-4 mb-6">
          <p className="text-xs text-gray-500 mb-1">Your invitation reference</p>
          <p className="font-mono font-bold text-golf-orange text-lg tracking-wider">{reference}</p>
        </div>
        <p className="text-xs text-gray-400">Please retain this reference for your records.</p>
      </motion.div>
    </div>
  );
}

function UnavailableScreen({ isClosed, isNotYetOpen, openDate, closeDate }: {
  isClosed: boolean;
  isNotYetOpen: boolean;
  openDate: string | null;
  closeDate: string | null;
}) {
  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-golf-sand flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
          {isClosed ? (
            <LockClosedIcon className="w-8 h-8 text-gray-400" />
          ) : (
            <ClockIcon className="w-8 h-8 text-gray-400" />
          )}
        </div>
        <h1 className="font-display text-2xl font-bold text-golf-gray mb-3">
          {isClosed ? 'Invitations Closed' : isNotYetOpen ? 'Coming Soon' : 'Not Available'}
        </h1>
        <p className="text-gray-500 leading-relaxed">
          {isClosed
            ? `RSVPs closed on ${closeDate ? formatDate(closeDate) : 'a past date'}. Thank you for your interest in the Ibom Open.`
            : isNotYetOpen
            ? `Invitations open on ${openDate ? formatDate(openDate) : 'a future date'}. Please check back then.`
            : 'This invitation is not currently available. Please check back later.'}
        </p>
      </div>
    </div>
  );
}
